import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import Select, { components as rsComponents } from "react-select";
import CreatableSelect from "react-select/creatable";
import { VariableSizeList } from "react-window";
import { Row, Col } from "reactstrap";
// import 'react-select/dist/react-select.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

import _ from "lodash";
import _InspectionDetails from "modules/project/qa/welding_inspections/web/form/3_InspectionDetails";

const OPTION_HEIGHT = 35;
const GROUP_HEIGHT = 25;

const customStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 10000,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 10000,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? (state.isFocused ? "#2684FF" : "#B2D4FF") : provided.backgroundColor,
    color: state.isSelected ? (state.isFocused ? "#fff" : "#1a1a1a") : provided.color,
    fontWeight: state.isSelected ? 600 : provided.fontWeight,
  }),
};

function flattenMenuItems(children) {
  const items = React.Children.toArray(children);
  const first = items[0];
  const grouped = React.isValidElement(first) && (first.props?.data?.options || []).length > 0;
  if (!grouped) return items;

  return items.reduce((acc, child) => {
    if (!React.isValidElement(child)) return acc;
    const nested = child.props.children;
    if (nested == null || typeof nested === "string") return acc.concat(child);
    return acc.concat(React.cloneElement(child, { type: "group" }, []), nested);
  }, []);
}

function itemHeight(item) {
  return item?.props?.type === "group" ? GROUP_HEIGHT : OPTION_HEIGHT;
}

// Default MenuList plus restore-on-select. react-select focuses the first option after
// a multi-select (option objects are often recreated), which scrolls the list to the top.
function makeDefaultMenuList(scrollLock) {
  return function DefaultMenuList(props) {
    const nodeRef = useRef(null);

    const setRef = (node) => {
      nodeRef.current = node;
      if (node && !node._lionScrollBound) {
        node.addEventListener(
          "scroll",
          () => {
            if (!scrollLock.restore) scrollLock.top = node.scrollTop;
          },
          { passive: true }
        );
        node._lionScrollBound = true;
      }
      if (typeof props.innerRef === "function") props.innerRef(node);
    };

    const restoreIfNeeded = () => {
      if (!scrollLock.restore || !nodeRef.current) return;
      nodeRef.current.scrollTop = scrollLock.top;
    };

    useLayoutEffect(restoreIfNeeded);
    useEffect(() => {
      restoreIfNeeded();
      const id = requestAnimationFrame(() => {
        restoreIfNeeded();
        scrollLock.restore = false;
      });
      return () => cancelAnimationFrame(id);
    });

    return <rsComponents.MenuList {...props} innerRef={setRef} />;
  };
}

// Windowed list without react-windowed-select's scrollToItem(focused) on every children
// update. That call is what jumps a virtual multi-select back to the top.
function makeWindowedMenuList(scrollLock) {
  return function WindowedMenuList(props) {
    const { children, innerRef, selectProps, getStyles } = props;
    const listRef = useRef(null);
    const items = useMemo(() => flattenMenuItems(children), [children]);
    const prevValue = useRef(selectProps.value);
    const prevFocused = useRef(-1);

    const menuListStyle = getStyles("menuList", props);
    const totalHeight = items.reduce((sum, item) => sum + itemHeight(item), 0) || OPTION_HEIGHT;
    const height = Math.min(menuListStyle.maxHeight || selectProps.maxMenuHeight || 300, totalHeight);
    const prefix = selectProps?.classNamePrefix;
    const className = prefix
      ? `${prefix}__menu-list${selectProps.isMulti ? ` ${prefix}__menu-list--is-multi` : ""}`
      : undefined;

    useEffect(() => {
      const focusedIdx = items.findIndex((item) => item.props?.isFocused);
      const valueChanged = prevValue.current !== selectProps.value;
      prevValue.current = selectProps.value;
      if (!listRef.current) return;

      if (valueChanged || scrollLock.restore) {
        const top = scrollLock.top;
        listRef.current.scrollTo(top);
        requestAnimationFrame(() => listRef.current?.scrollTo(top));
        scrollLock.restore = false;
        prevFocused.current = focusedIdx;
        return;
      }

      if (focusedIdx >= 0 && focusedIdx !== prevFocused.current) {
        listRef.current.scrollToItem(focusedIdx);
      }
      prevFocused.current = focusedIdx;
    }, [items, selectProps.value]);

    return (
      <VariableSizeList
        className={className}
        ref={listRef}
        outerRef={innerRef}
        height={height}
        width="100%"
        itemCount={items.length}
        overscanCount={4}
        itemSize={(index) => itemHeight(items[index])}
        itemKey={(index) => items[index]?.key ?? index}
        initialScrollOffset={scrollLock.top}
        onScroll={({ scrollOffset }) => {
          if (!scrollLock.restore) scrollLock.top = scrollOffset;
        }}
      >
        {({ index, style }) => <div style={style}>{items[index]}</div>}
      </VariableSizeList>
    );
  };
}

/**
 * Final Form field wrapper around react-select (windowed with react-window when virtualList is set).
 *
 * @param {Object} props
 * @param {Object} props.input - Final Form field input (`name`, `value`, `onChange`, `onBlur`, `onFocus`).
 * @param {Object} props.meta - Final Form field meta (`touched`, `error`, `warning`).
 * @param {Array<{value: *, label: string, options?: Array, isDisabled?: boolean}>} props.options - Select options (supports grouped `{ label, options }`).
 * @param {boolean} [props.multi] - Multi-select mode.
 * @param {Function} [props.modalToggle] - Click handler for the + button column (creates a side-by-side layout).
 * @param {string} [props.placeholder] - Placeholder text.
 * @param {boolean} [props.isClearable] - Whether the value can be cleared.
 * @param {string|number} [props.instanceId] - react-select instance id (defaults to `input.name`).
 * @param {boolean} [props.sendDestroy=false] - On multi remove, mark existing records with `_destroy` instead of dropping them.
 * @param {boolean} [props.saveObject=false] - Persist the full option object instead of just `option.value`.
 * @param {boolean} [props.disabled] - Disable the select.
 * @param {string} [props.classNamePrefix] - Suffix for `react-select-${classNamePrefix}` class prefix.
 * @param {Object} [props.components] - Custom react-select components.
 * @param {number} [props.maxOptionsSelected] - Cap selected options when `multi` is true.
 * @param {boolean} [props.closeMenuOnSelect] - Close the menu after selecting an option. Defaults to false when `multi` is set.
 * @param {string} [props.backupLabel] - Label shown when `value` is set but missing from `options`.
 * @param {string} [props.favourite_id] - Option key used to split Favourites vs Remaining Options groups.
 * @param {string|number} [props.tabIndex] - Tab index (non-virtual select only).
 * @param {HTMLElement} [props.menuPortalTarget] - Portal target for the menu.
 * @param {string} [props.menuPosition] - react-select menu position.
 * @param {boolean} [props.virtualList] - Window the option list (react-window) for large lists.
 * @param {number} [props.maxMenuHeight] - Max dropdown height in px (react-select default is 300).
 * @param {boolean} [props.creatable] - Allow typing a value that is not in options (react-select Creatable).
 * @param {Function} [props.formatCreateLabel] - Label for the create option when `creatable` is set.
 * @param {Function} [props.onChange] - Extra change callback (single-select only; after Final Form `onChange`).
 * @param {React.ReactNode} [props.children] - Optional content rendered in a side column.
 * @returns {JSX.Element}
 */
export default function ReactSelect(props) {
  const {
    input,
    meta,
    options,
    multi,
    modalToggle,
    placeholder,
    isClearable,
    instanceId: instanceIdProp,
    sendDestroy,
    saveObject,
    disabled,
    classNamePrefix,
    components,
    maxOptionsSelected,
    closeMenuOnSelect,
    backupLabel,
    favourite_id,
    tabIndex,
    menuPortalTarget,
    menuPosition,
    creatable,
    formatCreateLabel,
    virtualList,
    maxMenuHeight,
  } = props;
  const { name, value, onBlur, onChange, onFocus } = input;
  const instanceId = instanceIdProp ?? name;
  const { touched, error, warning } = meta;

  // Keep the same option object identity across renders. react-select tracks the
  // focused option by reference, so cloning options on each render jumps the menu to the top.
  const options_converted = useMemo(() => {
    const list = options || [];
    if (!favourite_id) return list;

    const favourite_options = _.filter(list, (o) => o[favourite_id] == true);
    if (_.isNotEmpty(favourite_options)) {
      return [
        { label: "Favourites", options: favourite_options },
        { label: "Remaining Options", options: _.filter(list, (o) => o[favourite_id] != true) },
      ];
    }
    return list;
  }, [options, favourite_id]);

  let transformedValue = transformValue(value, options_converted, multi, saveObject, creatable);

  const flatOptions = _.flatten(_.map(options_converted, (o) => (o.options ? o.options : o)));
  const findOption = _.find(flatOptions, (o) => o.value == value);
  if (!findOption && backupLabel) {
    transformedValue = { value: value, label: backupLabel };
  }

  let errorDisplay;
  if (_.isObject(error)) {
    errorDisplay = _.get(error, name);
  } else {
    errorDisplay = error;
  }

  const SelectComponent = creatable ? CreatableSelect : Select;
  const scrollLock = useRef({ top: 0, restore: false }).current;
  const MenuList = useMemo(
    () => (virtualList ? makeWindowedMenuList(scrollLock) : makeDefaultMenuList(scrollLock)),
    [virtualList, scrollLock]
  );
  const mergedComponents = { MenuList, ...components };
  const creatableProps = creatable
    ? { formatCreateLabel: formatCreateLabel || ((v) => `Use "${v}"`), createOptionPosition: "first" }
    : {};

  const selectedOptions = multi ? _.filter(value, (o) => !o?._destroy) : [];
  const isOptionDisabled = (option) => {
    if (option.isDisabled) return true;
    if (!(maxOptionsSelected && multi) || selectedOptions.length < maxOptionsSelected) return false;
    return !_.some(selectedOptions, (v) => v.value == option.value);
  };

  const handleChange = multi
    ? (values, action) => {
        scrollLock.restore = true;
        multiChangeHandler(onChange, sendDestroy, value)(values, action);
      }
    : singleChangeHandler(onChange, saveObject, props.onChange);

  return (
    <Fragment>
      <Row>
        <Col sm={modalToggle || props.children ? 10 : 12}>
          <SelectComponent
            classNamePrefix={classNamePrefix ? `react-select-${classNamePrefix}` : "react-select"}
            isClearable={isClearable}
            valueKey="value"
            name={name}
            value={transformedValue}
            multi={multi}
            isMulti={multi}
            options={options_converted}
            instanceId={instanceId}
            onChange={handleChange}
            onBlur={() => onBlur(value)}
            onFocus={onFocus}
            placeholder={placeholder}
            isDisabled={disabled}
            id={name}
            styles={customStyles}
            components={mergedComponents}
            closeMenuOnSelect={closeMenuOnSelect ?? !multi}
            hideSelectedOptions={false}
            blurInputOnSelect={multi ? false : undefined}
            isOptionDisabled={isOptionDisabled}
            tabIndex={virtualList ? undefined : tabIndex}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            maxMenuHeight={maxMenuHeight}
            {...creatableProps}
          />
        </Col>
        {modalToggle && (
          <Col sm={2} style={{ cursor: "pointer" }} onClick={modalToggle}>
            <FontAwesomeIcon icon={faPlus} />
          </Col>
        )}
        {props.children && <Col sm={2}>{props.children}</Col>}
      </Row>

      <Row>
        <Col>
          {touched &&
            ((errorDisplay && (
              <span className="text-danger">
                {name} {errorDisplay}
              </span>
            )) ||
              (warning && <span className="text-warning">{warning}</span>))}
        </Col>
      </Row>
    </Fragment>
  );
}

ReactSelect.defaultProps = {
  sendDestroy: false,
  saveObject: false,
};

/**
 * onChange from Redux Form Field has to be called explicity.
 */
function singleChangeHandler(func, saveObject, onChange) {
  return function handleSingleChange(value) {
    if (saveObject) {
      func(value ? value : null);
      onChange?.(value ? value : null);
    } else {
      func(value ? value.value : null);
      onChange?.(value ? value.value : null);
    }
  };
}

/**
 * onBlur from Redux Form Field has to be called explicity.
 */
function multiChangeHandler(finalFormOnChange, sendDestroy, finalFormValues) {
  return function handleMultiHandler(reactSelectValues, reactSelectAction) {
    const removedOption = reactSelectAction.removedValue || reactSelectAction.option;
    if (
      (reactSelectAction.action == "remove-value" || reactSelectAction.action == "deselect-option") &&
      sendDestroy &&
      removedOption?.id
    ) {
      let newArray = _.clone(finalFormValues ? finalFormValues : []);
      const findArrayKey = _.findKey(newArray, (item) => item.id == removedOption.id);
      if (findArrayKey != -1) {
        newArray[findArrayKey]._destroy = "1";
      }

      finalFormOnChange(newArray);
    } else if (reactSelectAction.action == "select-option" && sendDestroy) {
      let newArray = _.clone(finalFormValues ? finalFormValues : []);
      const findTheOption = _.findKey(finalFormValues, (ffv) => ffv.value == reactSelectAction.option.value);
      if (findTheOption && reactSelectAction.option?.id) {
        delete newArray[findTheOption]._destroy; // Deletes the destroy key
        finalFormOnChange(newArray);
      } else {
        newArray.push(reactSelectAction.option);
        finalFormOnChange(newArray);
      }
    } else {
      finalFormOnChange(reactSelectValues);
    }
  };
}

/**
 * For single select, Final Form keeps the value as a string, while React Select
 * wants the value in the form { value: "grape", label: "Grape" }
 *
 * * For multi select, Final Form keeps the value as array of strings, while React Select
 * wants the array of values in the form [{ value: "grape", label: "Grape" }]
 */
function transformValue(value, options, multi, saveObject, creatable) {
  if (multi && typeof value === "string") return [];

  let filteredOptions;
  if (multi) {
    filteredOptions = _.filter(value, (o) => !o._destroy);
  } else if (saveObject) {
    return value;
  } else if (value) {
    let clonedOptions = _.clone(options);
    const hasGroupedOptions = _.some(options, (o) => o.options);
    if (hasGroupedOptions) {
      clonedOptions = _.flatten(_.map(clonedOptions, (o) => (o.options ? o.options : o)));
    }
    filteredOptions = clonedOptions.find((option) => option.value == value);
    if (!filteredOptions && creatable) {
      filteredOptions = { value, label: value };
    }
  } else {
    filteredOptions = null;
  }

  return filteredOptions;
}
